import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../../../shared/services/utils.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { environment } from '../../../../../environments/environment';
import { ManagementClipResponse } from 'api-clipping';
import {
	ManagementContractNoticeAndAnnouncementResponse,
	ManagementSaleResponse,
	ManagementBankruptcyResponse,
} from 'api-contract-notices';
import { TranscriptionResponse } from 'api-transcription';

@Component({
	selector: 'app-client-data-card',
	standalone: true,
	imports: [
		CommonModule,
		MatCardModule,
		MatButtonModule,
		MatIconModule,
		MatTooltipModule,
		MatDividerModule,
		MatExpansionModule,
		MatCheckboxModule,
		TranslateModule,
	],
	templateUrl: './client-data-card.component.html',
	styleUrl: './client-data-card.component.scss',
})
export class ClientDataCardComponent {
	@Input() item: any;
	@Input() dataAdministrationType!: string;
	@Input() resellerSettingsContext: any;
	@Input() client: any;
	@Input() showTags = true;

	@Output() actionRequested = new EventEmitter<any>();

	externalAssets = environment.reseller.externalAssets;
	externalAssetsRoot = environment.reseller.externalAssetsRoot;

	constructor(
		public utils: UtilsService,
		public sharedService: SharedService,
		private translate: TranslateService
	) {}

	get typeSafe(): any {
		return this.utils.suppressType(this.item);
	}

	// Display field extraction
	getDisplayField(fieldName: string) {
		switch (this.dataAdministrationType) {
			case 'CLIPPING': {
				const item: ManagementClipResponse = this.item as ManagementClipResponse;
				if (fieldName === 'publishedDate') return this.utils.formatDate(item.publicationDate);
				if (fieldName === 'medium') return this.utils.getParameterByLanguageScript(item.medium, 'name');
				if (fieldName === 'mediumType') return item.medium?.type;
				if (fieldName === 'title') return this.utils.getParameterByLanguageScript(item, 'title', true);
				if (fieldName === 'brief') return this.utils.getParameterByLanguageScript(item, 'brief', true);
				if (fieldName === 'mediaUrl') {
					if (item.medium?.type === 'TV' || item.medium?.type === 'RADIO') {
						return item.medium?.mediaUrl ? item.medium?.mediaUrl : 'fallback_image';
					}
					return item.mediaUrl ? item.mediaUrl : item.medium?.mediaUrl ? item.medium?.mediaUrl : 'fallback_image';
				}
				if (fieldName === 'tags') return item.tags;
				if (fieldName === 'authors') {
					return item.authors?.map((a: any) => this.utils.getParameterByLanguageScript(a, 'name')).join(', ') || '';
				}
				if (fieldName === 'relatedCount') return this.utils.getParameterByName(this.item, 'relatedCount');
				break;
			}
			case 'CONTRACT_NOTICES':
			case 'CONTRACT_ANNOUNCEMENTS':
				return this.resolveContractField(this.item as ManagementContractNoticeAndAnnouncementResponse, fieldName);
			case 'SALES': {
				const item: ManagementSaleResponse = this.item as ManagementSaleResponse;
				if (fieldName === 'publishedDate') return this.utils.formatDate(item.publicationDate);
				if (fieldName === 'saleDate') return this.utils.formatDate(item.saleDate);
				if (fieldName === 'debtor') return item.debtors?.map(c => this.utils.getParameterByLanguageScript(c, 'name', true)).join(' | ') || '';
				if (fieldName === 'trustee') return item.trustees?.map(t => this.utils.getParameterByLanguageScript(t, 'name', true)).join(', ') || '';
				if (fieldName === 'placeOfSale') return item.placesOfSale?.join(', ') || '';
				if (fieldName === 'location') return item.locations?.join(', ') || '';
				if (fieldName === 'saleType') return item.type;
				break;
			}
			case 'BANKRUPTCIES': {
				const item: ManagementBankruptcyResponse = this.item as ManagementBankruptcyResponse;
				if (fieldName === 'publishedDate') return this.utils.formatDate(item.publicationDate);
				if (fieldName === 'company') return this.utils.getParameterByLanguageScript(item.company, 'name', true);
				if (fieldName === 'embs') return item.company?.embs || '';
				if (fieldName === 'phase') return item.phase;
				break;
			}
			case 'TRANSCRIPTIONS': {
				const item: any = this.item;
				if (fieldName === 'fileName') return item.fileName;
				if (fieldName === 'tags') return item.tags;
				if (fieldName === 'content') {
					if (item.content?.length) {
						return this.utils.truncateText(item.content.map((c: any) => c.text || '').join(' ').trim(), 700);
					}
					return '';
				}
				if (fieldName === 'createdAt') return this.utils.formatDate(item.createdAt);
				if (fieldName === 'lang') return item.lang?.toLowerCase();
				if (fieldName === 'url') return item.url || item.fileUrl || '';
				break;
			}
		}
		return;
	}

	/**
	 * Resolves a contract notice/announcement field for any item (the main card item
	 * or a related/connected item), so related items can render the same rich info.
	 */
	private resolveContractField(item: ManagementContractNoticeAndAnnouncementResponse, fieldName: string): any {
		if (!item) return '';
		if (fieldName === 'title') {
			if (item.noticeType) return this.utils.getParameterByLanguageScript(item, 'contractSubject', true);
			if (item.announcementType) return this.utils.getParameterByLanguageScript(item.contractNotice, 'contractSubject', true);
		}
		if (fieldName === 'medium') {
			const mediums: string[] = [];
			const sources = item.noticeType ? item.mediums : item.contractNotice?.mediums;
			sources?.forEach((m: any) => mediums.push(this.utils.getParameterByLanguageScript(m, 'name')));
			return mediums.join(', ');
		}
		if (fieldName === 'country') {
			const country = item.noticeType ? item.country : item.contractNotice?.country;
			return country ? country.toLowerCase() : '';
		}
		if (fieldName === 'publishedDate') return this.utils.formatDate(item.publishedDate);
		if (fieldName === 'deliveryDate') {
			if (item.noticeType && item.deliveryDate) return this.utils.formatDate(item.deliveryDate);
			return null;
		}
		if (fieldName === 'content') {
			if (item.noticeType) return this.utils.getParameterByLanguageScript(item.contractAuthority, 'name', true);
			return this.utils.getParameterByLanguageScript(item.contractNotice?.contractAuthority, 'name', true);
		}
		if (fieldName === 'type') {
			if (item.noticeType) return this.translate.instant('CONTRACT_NOTICES.' + item.noticeType);
			if (item.announcementType) return this.translate.instant('CONTRACT_ANNOUNCEMENTS.' + item.announcementType);
		}
		if (fieldName === 'estimatedPurchaseValue') {
			if (item.noticeType && item.estimatedPurchaseValue) {
				return `${this.utils.formatCurrency(item.estimatedPurchaseValue.toString())} ${item.currency}`;
			}
			return '';
		}
		if (fieldName === 'divisiblePurchase') {
			return item.noticeType ? item.divisiblePurchase : item.contractNotice?.divisiblePurchase;
		}
		if (fieldName === 'contractors') {
			if (item.announcementType && item.contractors) {
				return item.contractors.map(c => this.utils.getParameterByLanguageScript(c, 'name', true)).join(' | ');
			}
			return '';
		}
		if (fieldName === 'agreementValue') {
			if (item.announcementType && item.agreementValue) {
				return `${this.utils.formatCurrency(item.agreementValue.toString())} ${item.currency}`;
			}
			return '';
		}
		if (fieldName === 'eauction') return item.eauction;
		if (fieldName === 'procurementType') return item.procurementType;
		return '';
	}

	/** Same as resolveContractField but for a related/connected notice or announcement. */
	relatedField(relatedItem: ManagementContractNoticeAndAnnouncementResponse, fieldName: string): any {
		return this.resolveContractField(relatedItem, fieldName);
	}

	// Sentiment helpers
	getSentimentClass(sentimentType: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE', clip?: any): string {
		const target = clip ?? this.typeSafe;
		const score = target?.sentiment?.sentimentScore;
		if (score === undefined || score === null) return '';
		const currentType: 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' = score > 0 ? 'POSITIVE' : score < 0 ? 'NEGATIVE' : 'NEUTRAL';
		return currentType === sentimentType ? sentimentType.toLowerCase() + ' active' : '';
	}

	// Image URL check
	isImageUrl(url: string): boolean {
		if (!url) return false;
		return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);
	}

	getFileUrl(): string {
		const fileUrl = this.typeSafe.fileUrl;
		if (!fileUrl) return '';
		return fileUrl.startsWith('http') ? fileUrl : this.externalAssetsRoot + fileUrl;
	}

	getMediumTypeTranslation(): string {
		const mediumType = this.typeSafe.medium?.type;
		if (!mediumType) return '';
		return this.translate.instant('MEDIUM.TYPES.' + mediumType);
	}

	getMediumTypeIcon(): string {
		const type = this.typeSafe.medium?.type;
		if (!type) return 'public';
		switch (type) {
			case 'TV': return 'tv';
			case 'RADIO': return 'radio';
			case 'WEB':
			case 'PRINT_WEB': return 'public';
			case 'PRINT': return 'newspaper';
			default: return 'public';
		}
	}

	// Emit actions
	emitAction(action: string, data?: any) {
		this.actionRequested.emit({ action, item: this.item, ...data });
	}

	hasTranscriptionContent(): boolean {
		return this.item?.content?.length > 0;
	}

	/**
	 * Transcription tags to display — only those the client actually follows.
	 * The backend returns every tag assigned to the transcription (including ones outside the
	 * client's subscription), so filter against the followed set from the sidebar categories.
	 * Falls back to all tags while the followed set hasn't loaded yet.
	 */
	getFollowedTranscriptionTags(): any[] {
		const tags = this.getDisplayField('tags') as any[];
		if (!tags?.length) return [];
		const followed = this.sharedService.followedTranscriptionTagIds.getValue();
		if (!followed.size) return tags;
		return tags.filter(tag => tag?.id && followed.has(tag.id));
	}
}
